import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { PageEvent } from '@angular/material/paginator';
import { Observable, BehaviorSubject, map, share, combineLatest, startWith, debounceTime, tap, switchMap } from 'rxjs';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { AccessGroupIdTag, IdTag } from '../id-tag-model';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';

@Component({
  selector: 'adm-id-tag-access-groups-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    CustomDropDownArrowDirective,
    DkCurrencyPipe,
  ],
  templateUrl: './id-tag-access-groups-modal.component.html',
  styleUrl: './id-tag-access-groups-modal.component.scss',
})
export class IdTagAccessGroupsModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<IdTagAccessGroupsModalComponent>>(MatDialogRef);
  data = inject<{
    idTag: IdTag;
  }>(MAT_DIALOG_DATA);
  private accessGroupService = inject(AccessGroupService);
  private snackbarService = inject(SnackbarService);
  private idTagService = inject(IdTagService);

  public idTag: IdTag;
  private accessGroups$: Observable<AccessGroup[]>;
  public accessGroupIdTags$: Observable<AccessGroupIdTag[]>;
  public filteredAccessGroups$: Observable<AccessGroup[]>;

  public organizationContainsAccessGroups: boolean = true;
  public formGroup: FormGroup;
  public saving = false;
  public refreshSubject = new BehaviorSubject(null);
  public accessGroupIdTagAddedId: number | null;
  public accessGroupIdTagCreateModel: FormGroup = new FormGroup({
    typeAheadQuery: new FormControl<string | null | { identifier: string; name: string }>(null, { validators: [Validators.required] }),
  });
  public discountType: typeof DiscountType = DiscountType;

  async ngOnInit() {
    this.idTag = this.data?.idTag;
    this.formGroup = this.initFormGroup();

    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;

    const searchEventAccessGroup = new AccessGroupSearchEvent(pageEvent);
    searchEventAccessGroup.organizationId = this.idTag.organizationId;
    searchEventAccessGroup.carType = AccessGroupCarType.CompanyCar;
    this.accessGroups$ = this.accessGroupService.getAccessGroups(searchEventAccessGroup).pipe(
      map(result => result.items),
      share()
    );

    this.accessGroupIdTags$ = this.refreshSubject.pipe(
      switchMap(() => this.idTagService.getAccessGroups(this.idTag.id)),
      map(result => result.items),
      share()
    );

    this.accessGroups$ = combineLatest([
      this.accessGroupService.getAccessGroups(searchEventAccessGroup).pipe(
        map(result => result.items || []), // Ensure it's an array
        share()
      ),
      this.accessGroupIdTags$.pipe(startWith([] as AccessGroupIdTag[])), // Ensure it emits an empty array initially
    ]).pipe(
      map(([allGroups, allocatedGroups]) =>
        allGroups.filter(ag => !allocatedGroups.some(aag => aag.accessGroupIdentifier === ag.identifier))
      ),
      share()
    );

    this.filteredAccessGroups$ = combineLatest([
      this.accessGroups$,
      this.accessGroupIdTagCreateModel.controls.typeAheadQuery.valueChanges.pipe(
        startWith(this.accessGroupIdTagCreateModel.controls.typeAheadQuery.value),
        debounceTime(300)
      ),
    ]).pipe(
      tap(([initialAccessGroups, accessQuery]) => {
        if (initialAccessGroups.length === 0) {
          this.organizationContainsAccessGroups = false;
        }
        if (accessQuery && typeof accessQuery === 'object') {
          this.formGroup.controls.selectedAccessGroupIdentifier.setValue(accessQuery.identifier);
        } else {
          this.formGroup.controls.selectedAccessGroupIdentifier.setValue(null);
        }
      }),
      map(([accessGroups, accessQuery]) => {
        if (!accessQuery || typeof accessQuery === 'object') {
          return accessGroups;
        }
        return accessGroups.filter(
          a =>
            a.name.toLowerCase().indexOf(accessQuery.toLowerCase()) > -1 ||
            a.organizationName.toLowerCase().indexOf(accessQuery.toLowerCase()) > -1
        );
      })
    );
  }

  get accessGroupTypeAheadQuery() {
    return this.accessGroupIdTagCreateModel.controls['typeAheadQuery'];
  }
  get selectedAccessGroupIdentifier() {
    return this.formGroup?.controls['selectedAccessGroupIdentifier'];
  }

  public resetAccessGroup() {
    this.resetFormControl(this.selectedAccessGroupIdentifier);
    this.resetFormControl(this.accessGroupTypeAheadQuery);
  }

  public resetFormControl(control: AbstractControl): void {
    control.setValue(null);
    control.markAsUntouched();
    this.formGroup.patchValue({
      control: null,
    });
  }

  public accessGroupDisplayFn(accessGroup: AccessGroup): string {
    return accessGroup && accessGroup.name ? accessGroup.name : '';
  }

  public initFormGroup() {
    return new FormGroup({
      selectedAccessGroupIdentifier: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  public async saveAccessGroupToIdTag() {
    this.idTagService.saveAccessGroupToIdTag(this.idTag.id, this.formGroup.controls.selectedAccessGroupIdentifier.value).subscribe({
      next: () => {
        //this.accessGroupUserAddedIdentifier = id.toString();
        this.snackbarService.success(`Adgangsgruppen blev tilknyttet ${this.idTag.description}`);
        this.resetAccessGroup();
        this.refreshSubject.next(null);
      },
      error: err => {
        this.snackbarService.error(`Der skete fejl under tilknytningen. (${err.error.code})`);
      },
    });
  }

  public deleteAccessGroupIdTag(accessGroupIdentifier: string) {
    this.idTagService.deleteAccessGroupFromIdTag(this.idTag.id, accessGroupIdentifier).subscribe({
      next: () => {
        //this.accessGroupUserAddedIdentifier = id.toString();
        this.snackbarService.success(`Adgangsgruppen blev fjernet fra ${this.idTag.description}`);
        this.resetAccessGroup();
        this.refreshSubject.next(null);
      },
      error: err => {
        this.snackbarService.error(`Der skete fejl under sletning af tilknytning. (${err.error.code})`);
      },
    });
  }

  public close() {
    this.dialogRef.close({ idTagUpdated: this.idTag });
  }
}
